/**
 * English is the source of truth for every translation key.
 *
 * `Translations` is derived from this object and each other locale is typed
 * as `Translations`, so a key added here and forgotten elsewhere is a type
 * error and `pnpm typecheck` fails in CI before a missing string can reach
 * the browser. See docs/i18n.md.
 */

export const en = {
  common: {
    nav: {
      work: 'Work',
      approach: 'Approach',
      about: 'About',
      hire: 'Hire me',
      menu: 'Menu',
    },
    localeLabel: 'Language',
    skipToContent: 'Skip to content',
    portraitAlt: 'Ahmad El Masri',
    footer: {
      rights: '© 2026 Ahmad El Masri · Eindhoven, NL',
      photography: 'Portraits by {{photographer}}',
      // Every clause is a fact about the analytics in #28: the instance is
      // ours, the tracker sets no cookie, and a rotating daily salt makes
      // cross-day identity impossible. Deliberately not "we do not track
      // you", which would be untrue while a counter runs.
      privacy:
        'This site counts visits with self-hosted analytics. No cookies, no tracking across days, and nothing that identifies you.',
    },
    mobileBar: {
      status: 'Available for lead & advisory work',
      cta: 'Hire me',
    },
  },

  hero: {
    availability: 'Eindhoven · available for lead & advisory work',
    nameLine1: 'Ahmad',
    nameLine2: 'El Masri',
    lede: 'Ten years turning noisy machine data into systems people can trust.',
  },

  stats: {
    throughput: 'messages through the broker I rebuilt',
    database: 'faster database, same hardware',
    deploy: 'to deploy, down from a full day',
    engineers: 'engineers led across three products',
  },

  work: {
    titleLine1: 'Selected',
    titleLine2: 'work',
    lede: 'Three systems, each one a load problem that stopped being a load problem.',
    cases: {
      'sg-wireless': {
        title: 'A million messages a second, and room to spare',
        body: 'The broker saturated long before the device fleet did. I made the broker and proxy stateless so they scale on demand, split ingest from query traffic, and re-indexed for how the data was really read.',
      },
      withthegrid: {
        title: 'Knowing which grid asset fails next',
        body: 'Led the Asset Monitoring Platform and the team behind it — translating product intent into an architecture that holds as the asset count climbs.',
        outcome: 'Scaled without a rewrite.',
      },
      pycom: {
        title: 'Twenty engineers, three roadmaps, one pipeline',
        body: 'Joined as an engineer, left leading the group. We shipped on time by automating the boring parts — deploys went from a full day to under an hour.',
        outcome: 'Three roadmaps, all on schedule.',
      },
    },
  },

  approach: {
    title: 'How I work',
    lede: 'Four habits that survive contact with a real deadline.',
    habits: {
      measure: {
        title: 'Measure before you move',
        body: 'Find the real bottleneck before touching architecture. Half the time "we need a rewrite" turns out to be an index.',
      },
      boring: {
        title: 'Boring where it counts',
        body: 'Proven tools in the critical path. Keep the novelty for places where failure is cheap and reversible.',
      },
      slices: {
        title: 'Ship in slices',
        body: 'Small reversible steps behind a flag. The migration nobody noticed is the migration that worked.',
      },
      team: {
        title: 'Leave the team stronger',
        body: 'Written decisions, reviewed code, and people who can run the thing perfectly well without me.',
      },
    },
  },

  about: {
    whereTitle: "Where I've been",
    beyondTitle: 'Beyond the job',
    roles: {
      terramira: 'Software Engineer, geospatial',
      withthegrid: 'Senior Engineer & Team Lead',
      'sg-wireless': 'Senior Engineer & Team Lead',
      pycom: 'Engineer → Team Lead',
      iss: 'Beirut → Muscat',
    },
    beyond: {
      mentoring: {
        title: 'Mentoring',
        body: "Udacity project reviewer, Pluralsight mentor — four years reviewing other people's code.",
      },
      writing: {
        title: 'Writing',
        body: 'On scaling, team leadership and the boring parts of reliability.',
      },
      languages: {
        title: 'Languages',
        body: 'Arabic, English, Dutch, French',
      },
      education: {
        title: 'Education',
        body: 'MSc Nanotechnology, Grenoble · B.Eng, Beirut Arab University',
      },
    },
  },

  hire: {
    titleLine1: "Let's build",
    titleLine2: 'something solid',
    lede: 'Lead roles, contract work, or a second opinion on your architecture.',
    github: 'GitHub',
    linkedin: 'LinkedIn',
    cv: 'CV (PDF)',
  },

  offers: {
    architecture_review: {
      eyebrow: 'Most asked for',
      title: 'Architecture review',
      body: 'Two weeks. I read the code, talk to your engineers, and hand back a prioritised plan you can act on without me.',
      terms: 'Fixed fee · from €6k',
    },
    contract_lead: {
      eyebrow: 'Hands on',
      title: 'Contract lead',
      body: 'Two to six months embedded with your team — architecture, the hard code, and mentoring while we ship it.',
      terms: 'Day rate · discussed',
    },
    full_time: {
      eyebrow: 'Long term',
      title: 'Full-time role',
      body: "Lead or staff engineering in the Netherlands or EU-remote. Send the role and the team you'd want me to build.",
      terms: 'Not available right now',
    },
  },

  form: {
    title: 'Send the request',
    lede: 'Nothing more than this — no scheduling links, no forms behind forms.',
    engagementLegend: 'What kind of work',
    engagement: {
      architecture_review: 'Architecture review',
      contract_lead: 'Contract lead',
      full_time: 'Full-time role',
      talk_workshop: 'Talk / workshop',
    },
    name: 'Name',
    namePlaceholder: 'Your name',
    email: 'Email',
    emailPlaceholder: 'you@company.com',
    company: 'Company',
    companyOptional: '— optional',
    companyPlaceholder: 'Where you work',
    problem: "What's the problem?",
    problemPlaceholder: 'What are you building, and what is in the way?',
    when: 'When',
    budget: 'Budget',
    budgetOptional: '— optional',
    timing: {
      immediately: 'Immediately',
      within_a_month: 'Within a month',
      within_a_quarter: 'Within a quarter',
      exploring: 'Just exploring',
    },
    budgets: {
      prefer_to_discuss: 'Prefer to discuss',
      under_10k: 'Under €10k',
      from_10k_to_25k: '€10k – €25k',
      from_25k_to_75k: '€25k – €75k',
      over_75k: 'Over €75k',
    },
    submit: 'Send it',
    sending: 'Sending…',
    reassurance: "I'll reply personally within two working days. NDA on request.",
    dismiss: 'Dismiss',
    turnstileUnavailable:
      'The human check could not load. Disable your ad blocker, or email me directly.',
    validation: {
      name: 'Name is required',
      email: 'A valid email address is required',
      problem: 'Please describe the problem in a little more detail',
    },
    status: {
      successTitle: 'Request sent',
      success: 'Sent. Reference {{reference}} — I reply within two working days.',
      errorTitle: 'Not sent',
      verification: 'The human check did not pass. Please try again.',
      rateLimited: 'Too many requests. Please try again in a few minutes.',
      error: 'That could not be sent. Please email me directly at {{email}}.',
    },
  },

  next: {
    title: 'What happens next',
    replyNote: 'Eindhoven · replies in 2 days',
    steps: {
      read: { title: 'I read it', body: 'Personally, within two working days.' },
      answer: {
        title: 'A straight answer',
        body: "Yes, no, or “here's someone better for this”.",
      },
      call: {
        title: 'A 30-minute call',
        body: "Only if it's a fit. No pitch deck, no sales call.",
      },
    },
  },
} as const;

/** Structural type every other locale must satisfy. */
export type Translations = {
  [Namespace in keyof typeof en]: DeepMutable<(typeof en)[Namespace]>;
};

type DeepMutable<T> = T extends string
  ? string
  : { -readonly [Key in keyof T]: DeepMutable<T[Key]> };

export type Namespace = keyof typeof en;

export const NAMESPACES = [
  'common',
  'hero',
  'stats',
  'work',
  'approach',
  'about',
  'hire',
  'offers',
  'form',
  'next',
] as const satisfies readonly Namespace[];
